/**
 * The paths a signed-in person may reach in `work-api` (ADR-0070, ADR-0071).
 *
 * **Here, rather than in the gateway, because two processes enforce it.**
 * The gateway checks it so a browser gets a 404 at the edge; core-api
 * checks it again before forwarding, because core-api holds work-api's
 * shared token and an unchecked forward would make it an open proxy into
 * every route work-api has for anybody holding `IDENTITY_TOKEN`. A second
 * copy of this list would go stale the first time a route was added, which
 * is the defect this repository keeps finding, so there is one list and
 * both import it.
 *
 * An allow-list, for the same reason a manifest declares its surface: a
 * proxy that forwarded whatever path arrived would hand a browser every
 * route the service has, including `/v1/jobs/run` and `/v1/ingest/gmail` —
 * neither of which is a person's to call. A path not on this list is a 404
 * here rather than a refusal there.
 */
export const APP_PATHS = new Set([
    // Today, and moving things around in it.
    '/v1/today',
    '/v1/today/workspace',
    '/v1/today/record/call',
    // Their own calendar, and the brief for whatever they are looking at.
    '/v1/calendar/events',
    // When they could be booked (S1): the slots `bookableSlots` computes from
    // their own availability rules, and the rules themselves. A person sets
    // when they are willing to be booked; a calendar being empty is not that.
    '/v1/calendar/slots',
    '/v1/availability/get',
    '/v1/availability/set',
    '/v1/briefs/live',
    '/v1/briefs/compose',
    '/v1/today/place',
    '/v1/today/undo',
    // Since you left (ADR-0088): an arrival is recorded when Today opens, and
    // the recap is composed from records since the previous sitting ended.
    '/v1/today/arrive',
    '/v1/today/recap',
    // The department's pipeline, from its own Deal records (proposal 0008).
    '/v1/insight/pipeline',
    // What a Today item points at, by kind: the department's threads, the
    // person's tasks, the mailboxes the department works out of. Lists, so
    // a screen resolves a source id to a title in one call rather than one
    // per row (S1).
    '/v1/threads/list',
    '/v1/tasks/list',
    '/v1/tasks/reviews',
    '/v1/tasks/reviews/postpone',
    '/v1/tasks/get',
    '/v1/tasks/close',
    // A task's own page and board (ADR-0149): its words, what is said about
    // it, the agent's elaboration, and its time in the calendar.
    '/v1/tasks/board',
    '/v1/tasks/update',
    // A task made for, or handed on to, a named person or agent here (ADR-0157).
    '/v1/tasks/create',
    '/v1/tasks/assign',
    '/v1/tasks/assignees',
    // Removed with why, and what keeps being removed offered as a rule or a
    // direction (ADR-0158); a person's directions to the system and to the
    // department's agents (ADR-0159).
    '/v1/tasks/remove',
    '/v1/threads/remove',
    '/v1/mail-rules/list',
    '/v1/mail-rules/add',
    '/v1/mail-rules/revoke',
    '/v1/removals/decline',
    '/v1/directions/list',
    '/v1/directions/add',
    '/v1/directions/revoke',
    // Who is away or part-time, and when a plan rests (proposal 0012, E2, E14).
    '/v1/away/list',
    '/v1/away/set',
    '/v1/away/end',
    '/v1/plans/rests',
    // Suppliers compared and their samples judged; what buyers said, and the
    // demand it adds up to (proposal 0012, E4, E5).
    '/v1/suppliers/compare',
    '/v1/suppliers/samples',
    '/v1/suppliers/samples/receive',
    '/v1/suppliers/samples/judge',
    '/v1/buyers/answers',
    '/v1/buyers/answers/add',
    '/v1/demand/scorecard',
    // A department's registers: agreements, events, funding, what a market
    // asks (proposal 0012, E9–E12).
    '/v1/registers/list',
    '/v1/registers/write',
    '/v1/institutions/filing/configure',
    '/v1/institutions/filing/run',
    '/v1/institutions/documents/check',
    '/v1/institutions/drafts/reconcile',
    '/v1/institutions/list',
    '/v1/institutions/read',
    '/v1/institutions/drafts/reference',
    '/v1/institutions/create',
    '/v1/institutions/configure',
    '/v1/institutions/own-mail/options',
    '/v1/institutions/own-mail/link',
    '/v1/institutions/proposals/capture',
    '/v1/institutions/proposals/decide',
    '/v1/institutions/assessments/record',
    '/v1/institutions/portal/review',
    '/v1/institutions/contacts/record',
    '/v1/institutions/requirements/revise',
    '/v1/institutions/packages/draft',
    '/v1/institutions/packages/review',
    '/v1/institutions/submissions/record',
    '/v1/institutions/interactions/record',
    '/v1/institutions/decisions/record',
    // Probing other kinds of buyer, capped, and what came of each kind; the
    // first email read by a native reader; what threatens an origin; what
    // came of a call (proposal 0012, E7, E8, E13, E16).
    '/v1/plans/probe-cap',
    '/v1/plans/probes',
    '/v1/outreach/sequences/reviews',
    '/v1/outreach/sequences/review/ask',
    '/v1/outreach/sequences/review/answer',
    '/v1/signals/supply-risks',
    '/v1/signals/supply-risk/add',
    '/v1/cadences/call-outcome',
    // Finding manufacturers, screening them and choosing a main and a backup
    // (proposal 0013, ADR-0162).
    '/v1/suppliers/research',
    '/v1/suppliers/candidates',
    '/v1/suppliers/candidates/add',
    '/v1/suppliers/contacts/add',
    '/v1/suppliers/decide',
    '/v1/suppliers/decisions',
    '/v1/suppliers/prepare-email',
    '/v1/suppliers/contacted',
    '/v1/automations/list',
    '/v1/automations/decide',
    '/v1/tasks/notes/list',
    '/v1/tasks/notes/add',
    '/v1/tasks/elaborate',
    '/v1/tasks/approve-held',
    // The department's agent says more about any record a person asked about (ADR-0165).
    '/v1/records/elaborate',
    '/v1/records/elaborations',
    // What a market's research is good for: found on a person's press, taken by the lead (ADR-0175).
    '/v1/requests/uses/find',
    '/v1/requests/uses',
    '/v1/requests/uses/decide',
    // Nia finds distributors in one market, each from its own site (ADR-0176).
    '/v1/distributors/research',
    // A person makes a task of what they are looking at (ADR-0155).
    '/v1/tasks/add',
    // What an assistant a person signed in asks and proposes (ADR-0153).
    '/v1/prospects/check',
    '/v1/shortlists/propose',
    '/v1/shortlists/list',
    '/v1/shortlists/get',
    '/v1/shortlists/decide',
    '/v1/evidence/propose',
    '/v1/evidence/proposals',
    '/v1/evidence/decide',
    '/v1/prospecting/brief',
    '/v1/strategies/propose',
    '/v1/strategies/list',
    '/v1/strategies/decide',
    '/v1/skills/list',
    '/v1/index/search',
    '/v1/index/fetch',
    '/v1/tasks/schedule',
    '/v1/drafts/redraft',
    '/v1/guidance/list',
    '/v1/guidance/add',
    '/v1/guidance/decide',
    '/v1/guidance/revoke',
    '/v1/mailboxes/list',
    // Mail: reading threads and what is on them.
    '/v1/threads/open',
    '/v1/threads/read',
    '/v1/threads/resolve',
    '/v1/threads/claim',
    '/v1/threads/unclaim',
    '/v1/subjects/history',
    '/v1/subjects/confirm',
    '/v1/subjects/reviews',
    '/v1/subjects/decide',
    '/v1/subjects/evaluations/queue',
    '/v1/subjects/evaluations/backfill',
    '/v1/subjects/policies',
    '/v1/subjects/policies/enable',
    '/v1/subjects/policies/stop',
    '/v1/subjects/resolve-address',
    // Asking for a draft, the ones waiting on a person, and their decision.
    // `compose` only enqueues: the model call and the Gmail write happen in
    // the heartbeat, so what comes back is a job id and `pending` is where
    // the draft appears a minute later.
    '/v1/drafts/compose',
    '/v1/drafts/pending',
    '/v1/drafts/decide',
    '/v1/drafts/authority',
    // A person's own credential for working without a browser (migration
    // 0039). On the list because minting one is something a person does for
    // themselves; the plaintext is returned once and never again.
    '/v1/tokens/mint',
    '/v1/tokens/list',
    '/v1/tokens/revoke',
    // The side chat.
    '/v1/chat/open',
    '/v1/chat/say',
    '/v1/chat/read',
    '/v1/chat/close',
    '/v1/chat/context',
    // Who a person may talk to, and making what they asked a department agent
    // its task (ADR-0089). Nothing runs from a chat turn itself.
    '/v1/chat/agents',
    '/v1/chat/task',
    // A twin's task (Amendment 0011, ADR-0139): planned, confirmed by its
    // person, and read back. A named unapproved company goes to Nia instead.
    '/v1/chat/material/list',
    '/v1/chat/material/upload',
    '/v1/chat/material/drive',
    '/v1/chat/twin-plan',
    '/v1/chat/twin-plan/latest',
    '/v1/chat/twin-plan/read',
    '/v1/chat/twin-task',
    '/v1/chat/twin-handoff',
    '/v1/tasks/twin/list',
    '/v1/tasks/twin/read',
    // People talking to each other (ADR-0089): a conversation is its two
    // members'; a twin drafts in it and never sends.
    '/v1/conversations/list',
    '/v1/conversations/open',
    '/v1/conversations/read',
    '/v1/conversations/send',
    '/v1/conversations/draft',
    // Unresolved mail, and promoting a stranger to a contact.
    '/v1/unresolved/list',
    '/v1/unresolved/research/policies/enable',
    '/v1/unresolved/research/policies/stop',
    '/v1/unresolved/research/queue',
    '/v1/unresolved/research/priority',
    '/v1/unresolved/dismiss',
    '/v1/unresolved/promote',
    // When in their own day a person is told who wrote to them (ADR-0065).
    '/v1/unresolved/digest-time',
    // The thread page and the review queue (S2).
    '/v1/threads/get',
    '/v1/drafts/list',
    // The fixed core as records: read anywhere, edited by a person outside a
    // department (0007), approved as a counterparty under a token (ADR-0063).
    '/v1/directory/list',
    '/v1/directory/get',
    '/v1/directory/create',
    '/v1/companies/workspace',
    '/v1/companies/operations',
    '/v1/companies/roles/record',
    '/v1/companies/groups/link',
    '/v1/companies/enrichment/propose',
    '/v1/companies/enrichment/decide',
    '/v1/companies/sources/refresh',
    '/v1/companies/backfill',
    '/v1/platform/gcp-costs',
    '/v1/platform/gcp-costs/record',
    '/v1/communications/capabilities',
    '/v1/communications/tasks/create',
    '/v1/communications/connections/import',
    '/v1/communications/manual/list',
    '/v1/communications/manual/create',
    '/v1/communications/manual/outcomes',
    '/v1/communications/manual/attachments/upload',
    '/v1/communications/manual/attachments/download',
    '/v1/companies/record/call',
    '/v1/directory/candidates',
    '/v1/duplicates/list',
    '/v1/duplicates/evaluations/queue',
    '/v1/duplicates/evaluations/backfill',
    '/v1/duplicates/decide',
    '/v1/duplicates/consolidate',
    '/v1/duplicates/reverse',
    '/v1/duplicates/canonical',
    '/v1/duplicates/history',
    '/v1/duplicates/policies',
    '/v1/duplicates/policies/enable',
    '/v1/duplicates/policies/stop',
    '/v1/contacts/update',
    '/v1/contacts/assign',
    '/v1/companies/update',
    '/v1/employments/move',
    '/v1/counterparties/approve',
    // A stranger known and approved in one act (ADR-0065, Q14), under a
    // `counterparty.approve` token bound to the held line; core-api answers it.
    '/v1/unresolved/know-and-approve',
    // What has happened around a record (ADR-0029).
    '/v1/records/incoming',
    // The flexible layer, read for the department whose feature owns it (§5).
    '/v1/flex/entities',
    '/v1/flex/list',
    '/v1/flex/get',
    // A person writes a record of their department's own kinds (ADR-0110).
    '/v1/flex/write',
    // Work between departments (§18).
    '/v1/requests/list',
    '/v1/requests/read',
    '/v1/requests/open',
    '/v1/requests/acknowledge',
    // A suggestion for the page in view, to Norum Tech (0132).
    '/v1/suggestions/open',
    '/v1/requests/complete',
    '/v1/requests/refuse',
    '/v1/requests/cancel',
    // What happened, for a person looking back at it.
    '/v1/reports/activity',
    // Outbound outreach, read (proposal 0007): the market calendar, and the
    // suppression register as counts and a one-address check. Neither sends
    // or changes anything; the register holds hashes, so there is no list.
    '/v1/outreach/markets',
    // Markets, researched from their sources (ADR-0134, migration 0105).
    '/v1/markets/list',
    '/v1/markets/screen',
    '/v1/markets/get',
    '/v1/markets/decide',
    '/v1/markets/confirm-route',
    '/v1/markets/evidence',
    '/v1/markets/research',
    '/v1/markets/sources',
    '/v1/markets/report',
    // A report filed to the shared drive by the mirror (ADR-0135 §4, migration 0107).
    '/v1/documents/create',
    '/v1/documents/list',
    '/v1/documents/read',
    // Follow-through (ADR-0136, migration 0108).
    '/v1/cadences/list',
    '/v1/intros/list',
    '/v1/intros/answer',
    '/v1/prospects/sample',
    '/v1/samples/list',
    '/v1/samples/outcome',
    '/v1/prospects/opt-out',
    '/v1/prospects/details',
    '/v1/companies/details',
    '/v1/prospects/erase',
    // The prospecting report (proposal 0009 B6).
    '/v1/reports/prospecting',
    // Market validation (ADR-0135, migration 0109).
    '/v1/buyers/list',
    '/v1/buyers/create',
    '/v1/buyers/approve',
    '/v1/suppliers/list',
    '/v1/suppliers/get',
    '/v1/suppliers/create',
    '/v1/suppliers/answer',
    '/v1/suppliers/confirm',
    '/v1/suppliers/ask',
    '/v1/suppliers/asked',
    '/v1/suppliers/contact',
    '/v1/suppliers/find-contact',
    '/v1/offers/list',
    '/v1/offers/get',
    '/v1/offers/create',
    '/v1/offers/prepare',
    '/v1/offers/sent',
    '/v1/offers/send-to',
    '/v1/offers/recipient-sent',
    // Prospecting (ADR-0134, migration 0106).
    '/v1/profiles/list',
    '/v1/profiles/create',
    '/v1/profiles/suggest',
    '/v1/profiles/live',
    '/v1/profile-changes/list',
    '/v1/profile-changes/decide',
    '/v1/territories/list',
    '/v1/territories/get',
    '/v1/territories/create',
    '/v1/plans/list',
    '/v1/plans/create',
    '/v1/plans/dry-run',
    '/v1/prospecting/setup',
    '/v1/signals/list',
    '/v1/prospecting/batch',
    '/v1/prospecting/index',
    '/v1/prospects/lost',
    '/v1/prospects/objection',
    '/v1/prospects/approve-company',
    '/v1/prospects/intro',
    '/v1/outreach/suppression',
    '/v1/outreach/suppression/check',
    // Prospects (ADR-0064): found, read and enriched by the agent holding the
    // department's prospecting grant, and drafted into its shared box. A
    // person starts, excludes, imports and asks for a draft; nothing sends,
    // and no response carries an address.
    '/v1/outreach/discover',
    '/v1/outreach/prospects',
    '/v1/outreach/prospect',
    '/v1/outreach/prospect/exclude',
    '/v1/outreach/prospect/draft',
    '/v1/outreach/prospect/retry',
    '/v1/outreach/prospects/import',
    // Campaigns (ADR-0079, ADR-0086): drawn up, approved under a
    // `campaign.approve` token (answered by core-api), ended without one.
    // The queue is the first touches with their authority line, and a
    // complaint is a person marking a reply as one.
    '/v1/outreach/campaigns',
    '/v1/outreach/campaign',
    '/v1/outreach/campaigns/create',
    '/v1/outreach/campaigns/approve',
    '/v1/outreach/campaign/end',
    '/v1/outreach/queue',
    '/v1/outreach/complaint',
    // Lifecycle (ADR-0082, ADR-0087): who Customer.io may be told about, on
    // what evidence, and what it said back; and last week, summarised.
    '/v1/lifecycle/contacts',
    '/v1/lifecycle/contact',
    '/v1/lifecycle/record',
    '/v1/lifecycle/withdraw',
    '/v1/lifecycle/import',
    '/v1/lifecycle/sync',
    '/v1/lifecycle/track',
    // Mailings (ADR-0099): written by a member, approved under a
    // `mailing.approve` token (answered by core-api), ended without one; and
    // what journeys asked for, with what came of each.
    '/v1/mailings',
    '/v1/mailing',
    '/v1/mailings/create',
    '/v1/mailings/approve',
    '/v1/mailing/end',
    '/v1/mailing/requests',
    '/v1/outreach/report',
    // Oversight, read (S4, ADR-0073). Every one of these derives who is
    // asking from the database and shows a lead their departments and a
    // superadmin everything; the body carries no flag that could widen it.
    '/v1/approvals/queue',
    // Everything the approvals queue does not carry that waits on a
    // superadmin, for the Workspace (ADR-0117). A superadmin's read.
    '/v1/workspace/queue',
    // Where a request or a task stands and why, from the platform's own rows
    // (ADR-0169); and the platform's own health, the superadmin's.
    '/v1/requests/standing',
    '/v1/tasks/standing',
    '/v1/platform/health',
    // A superadmin's twin keeping the platform (Amendment 0013, ADR-0172): the
    // catalogue, the ledger, a read asked for or a write proposed, and the
    // standing assignment a superadmin confirms. Each refuses anyone else.
    '/v1/operations/catalogue',
    '/v1/operations/list',
    '/v1/operations/read',
    '/v1/operations/run',
    '/v1/operations/standing',
    '/v1/operations/standing/stop',
    // The nightly audit's switch and its nights, and the briefs it wrote (ADR-0174).
    '/v1/operations/nightly',
    '/v1/operations/nightly/read',
    '/v1/briefs/list',
    '/v1/briefs/read',
    '/v1/agents/list',
    '/v1/agents/get',
    '/v1/assignments/list',
    '/v1/departments/list',
    '/v1/departments/get',
    '/v1/tools/list',
    // What a department's agents may use, as the gateway decides it (ADR-0134).
    '/v1/tools/attachments',
    '/v1/tools/where',
    // The territory map's public browser key (Amendment 0010, ADR-0137):
    // handed to a signed-in person's browser, never to an agent, only once
    // its restrictions read back from Google, and counted as a map load.
    '/v1/maps/key',
    '/v1/audit/list',
    // A person's own twin, in the department, project or task in view (0122).
    '/v1/audit/twin',
    '/v1/audit/verify',
    '/v1/cost/summary',
    '/v1/rollback/triggers',
    '/v1/rollback/readings',
    '/v1/promotions/list',
    // Make it mine, and put it back (S5; §9, ADR-0028, ADR-0062, §13).
    // A person's own line of the experience plane, their rules and
    // preferences for their twin, the toolset they chose, and the ledger
    // read as text. Every write here is to the person's own surfaces; the
    // one that reaches others — deciding a promotion — checks the scope §9
    // names at the database.
    '/v1/experience/line',
    '/v1/experience/personalise',
    '/v1/experience/reset',
    '/v1/promotions/propose',
    '/v1/promotions/decide',
    '/v1/rules/list',
    '/v1/rules/add',
    '/v1/rules/revoke',
    '/v1/preferences/list',
    '/v1/preferences/add',
    '/v1/preferences/revoke',
    '/v1/toolsets/list',
    '/v1/toolsets/mine',
    '/v1/toolsets/self-select',
    '/v1/toolsets/define',
    '/v1/ledger/constitution',
    '/v1/ledger/decisions',
    '/v1/ledger/decision',
    // The change loop (S6, ADR-0074). A person asks and confirms their own
    // read-back; the lead or a superadmin approves or sends back. Which of
    // them may is the database's answer, from the session's email.
    '/v1/loop/start',
    '/v1/loop/list',
    '/v1/loop/status',
    '/v1/loop/preview',
    '/v1/loop/confirm',
    '/v1/loop/abandon',
    '/v1/builds/approve',
    '/v1/builds/return',
    // Two conversations as one (ADR-0031): proposed on a person's day by the
    // heartbeat, merged or kept apart by that person; or merged directly by a
    // person who found the pair, with a reason.
    '/v1/merges/list',
    '/v1/merges/decide',
    '/v1/threads/merge',
    // Projects and a voice earned inside one (ADR-0059): a person opens one
    // and adds colleagues, asks their twin for a draft where a project
    // assignment says it may, and says what they did with each draft.
    '/v1/projects/list',
    '/v1/projects/get',
    '/v1/projects/create',
    '/v1/projects/add-member',
    '/v1/projects/draft',
    '/v1/publications/decide',
    // A project is a room its participants share (Amendment 0009, ADR-0125):
    // what they said, a person saying something, and a meeting convened on it.
    '/v1/projects/room',
    '/v1/projects/room/say',
    '/v1/projects/meetings/convene',
    // A hold on a person's own calendar, placed by their twin (ADR-0067), and
    // its invitations, which only the person's own press sends (ADR-0093).
    '/v1/calendar/hold',
    '/v1/calendar/release',
    '/v1/calendar/invite',
    // Move or cancel a meeting whose invitations went out (ADR-0121). The person's press only.
    '/v1/calendar/move',
    '/v1/calendar/cancel',
    // The calendars a person follows, and what is on them (ADR-0094).
    '/v1/calendar/following',
    // Knowing what happened (S7, 0049): a person's own notifications, and the
    // incidents a rollback trigger caught, for those who oversee them.
    '/v1/notifications/list',
    '/v1/notifications/read',
    '/v1/incidents/list',
    '/v1/incidents/get',
    '/v1/incidents/acknowledge',
    // A Drive edit to the ledger, kept as a proposal for a superadmin (0053).
    '/v1/ledger/proposals',
    '/v1/ledger/proposal',
    '/v1/ledger/proposals/decide',
    // Every open one shown to be the mirror's own write, declined in one press (0183).
    '/v1/ledger/proposals/decline-mirrors-own',
    // People (S8, ADR-0057): who works here and who did, and the drive's
    // folders by key that a departure files knowledge to (ADR-0038).
    '/v1/people/list',
    '/v1/people/get',
    '/v1/people/invitations',
    '/v1/drive/folders',
    // A folder's files, read live as the person asking under their own drive
    // consent (ADR-0090, ADR-0091).
    '/v1/drive/files',
    // Capture, per meeting, and the commitments the person confirms from its
    // notes (ADR-0092).
    '/v1/calendar/capture',
    '/v1/commitments/list',
    '/v1/commitments/confirm',
    '/v1/commitments/dismiss',
    // A data subject's request to be forgotten, recorded by whoever received it.
    '/v1/erasure/list',
    '/v1/erasure/get',
    '/v1/erasure/request',
    // Somebody on the suppression list wants to hear from us again (ADR-0111):
    // anyone records the ask with its evidence; a superadmin lifts it below.
    '/v1/outreach/suppression/lift-request',
    '/v1/outreach/suppression/lift-requests',
    // First-touch templates outside a campaign (ADR-0112): a member writes,
    // the lead approves, and a draft still waits for a person to send.
    '/v1/outreach/sequences',
    '/v1/outreach/sequences/create',
    '/v1/outreach/sequences/approve',
    '/v1/outreach/sequences/retire',
    // Cover while a person is away (ADR-0114): set by them, their lead or a
    // superadmin; it moves new tasks and never visibility. And a person's
    // first days, read from the platform's own state (Stage 12).
    '/v1/cover/list',
    '/v1/cover/set',
    '/v1/cover/end',
    '/v1/people/onboarding',
    // A file that goes with a draft (ADR-0114): uploaded to the platform's
    // own EU store, or named from the department's Drive folder listing;
    // either way it leaves by a link written into the body the person reads.
    '/v1/attachments/list',
    '/v1/attachments/upload',
    '/v1/attachments/drive',
    // A person's booking links and the bookings made from them (ADR-0114).
    '/v1/booking-links',
    '/v1/booking-links/create',
    '/v1/booking-links/retire',
    '/v1/bookings/decide',
]);
/**
 * The paths a superadmin reaches through the admin door, `/v1/admin/*`
 * (ADR-0073). Disjoint from `APP_PATHS` by construction — a test asserts
 * it — because the two doors have different locks: this one admits a
 * session cookie only, never a personal API token, and only a superadmin's.
 * The gateway checks the list and core-api answers every path itself; none
 * is carried to work-api, which holds no way to spend a supervision token.
 *
 * `controls/state` is here rather than on the person's list because it is
 * the one read that must keep answering while the kill switch is engaged —
 * the switch is released from the screen that reads it.
 */
export const ADMIN_PATHS = new Set([
    '/v1/supervision/mint',
    '/v1/controls/state',
    '/v1/controls/kill',
    '/v1/agents/revoke',
    '/v1/agents/restore',
    '/v1/departments/suspend',
    '/v1/departments/resume',
    // ADR-0124: a superadmin creates a department live, under a
    // `department.create` token bound to its name. No PR, no reviewer —
    // the token and the receipt stand in for the second human.
    '/v1/departments/create',
    // ADR-0151: an agent added to a department live, under an `agent.create`
    // token bound to `department:<name>:agent:<given>`; its ledger line is
    // committed as the GitHub App, and org:sync creates it on the next deploy.
    '/v1/agents/create',
    // Amendment 0012, ADR-0153: a department's shortlists confirming
    // themselves on their record. Stopping is at once and spends no token;
    // starting again spends a `shortlist.autoconfirm` token bound to
    // `autoconfirm:<department>`, and the record counts from that start.
    '/v1/autoconfirm/stop',
    '/v1/autoconfirm/start',
    // Amendment 0013, ADR-0172: a superadmin's write to the platform's own
    // project. A sensitive one on their press, a critical one under an
    // `operation.run` token bound to `operation:<id>`; refusing narrows and
    // spends none. A sensitive one's record stops at once and starts again
    // under an `operation.earned_start` token bound to `operation-kind:<op>`.
    '/v1/operations/perform',
    '/v1/operations/refuse',
    '/v1/operations/earned',
    // A brief opened as an issue on the platform's own repository, by the
    // superadmin's press, once (ADR-0174). Opening confers nothing: no token.
    '/v1/briefs/open-issue',
    '/v1/assignments/grant',
    // A prospecting plan is an assignment the superadmin grants (ADR-0134).
    '/v1/plans/approve',
    '/v1/assignments/end',
    // Amendment 0008, ADR-0123: a project lending one of its own tools to a
    // participant outside its department, under a `project.tool_grant` token
    // bound to the project and the tool. Revoking narrows, so it spends none —
    // the same asymmetry `/v1/assignments/end` already has against `/grant`.
    '/v1/projects/tools/grant',
    '/v1/projects/tools/revoke',
    // Amendment 0011, ADR-0139: one tool for one twin's task, under a
    // `task.tool_grant` token bound to the pair; refusing and revoking narrow,
    // and spend none. Earned granting stops at once and starts under a token.
    '/v1/tasks/tools/grant',
    '/v1/tasks/tools/refuse',
    '/v1/tasks/tools/revoke',
    '/v1/tasks/tools/earned',
    '/v1/tasks/tools/pending',
    // Where a blocker notice goes: a Google Chat space, a Telegram chat.
    '/v1/notify/channels/add',
    '/v1/notify/channels/remove',
    // ADR-0123 §5: a project one department leads, or none, that people and
    // agents from any department join. Opening one spends no token (a
    // person opens one in their own department without one, too); putting
    // someone on it does, bound to the project and that participant, because
    // membership is what a project's grants reach. Removing narrows: none.
    '/v1/projects/open',
    '/v1/projects/members/add',
    '/v1/projects/members/remove',
    '/v1/autonomy/set',
    '/v1/tools/admit',
    '/v1/tools/withdraw',
    // A suggestion for a page, decided once, and an accepted one opened as an issue (ADR-0150).
    '/v1/suggestions/decide',
    // An accepted suggestion's issue, opened after the decision (ADR-0156).
    '/v1/suggestions/open-issue',
    // ADR-0142: a hosted assistant (ChatGPT) signs in as a person only once
    // the superadmin has admitted its vendor as a client, under a
    // `client.admit` token bound to `client:<vendor>`, with the exact
    // redirect set the vendor publishes. Withdrawing narrows, so it spends
    // none; the list is a read.
    '/v1/hosted-clients/list',
    '/v1/hosted-clients/admit',
    '/v1/hosted-clients/withdraw',
    // A preset is what a superadmin decides a function starts with (§13).
    // No token: it confers nothing an assignment has not, and a preset a
    // person is given still resolves through the library and the gateway.
    '/v1/toolsets/preset',
    '/v1/toolsets/assign',
    // A person leaves: access ends, their work goes to a named successor
    // (ADR-0057 as amended). An `identity.revoke_human` token bound to them.
    '/v1/humans/revoke',
    // A data subject forgotten (ADR-0015, ADR-0030; 0058): executed under a
    // `data.erase` token bound to the request, or refused with a reason —
    // which takes nothing away and spends no token.
    '/v1/erasure/execute',
    '/v1/erasure/refuse',
    // Lifting a suppression (ADR-0111): under a `suppression.lift` token bound
    // to `suppression_lift:<request id>`, which the database checks again;
    // refusing takes nothing away and spends none.
    '/v1/suppression/lift',
    '/v1/suppression/refuse',
    // Someone should join (0059): checked against the organisation's rules,
    // with the roster lines a person commits. Confers nothing, so no token.
    '/v1/people/invite',
    '/v1/people/withdraw',
    // A connection's key is a superadmin's (ADR-0083): set and rotate spend a
    // `secret.rotate` token bound to `connection:<ref>`; revoke takes away and
    // spends none; the list names state and holders and never a value.
    '/v1/connections/list',
    '/v1/connections/set',
    '/v1/connections/rotate',
    '/v1/connections/revoke',
    '/v1/connections/rotation-term',
    // How long an assignment may run (ADR-0085): lengthening spends an
    // `iam.grant_scope` token bound to `term:<level>:<ref>`; shortening none.
    '/v1/terms/set',
    // What each vendor gives free, and what paid use a superadmin allowed
    // beyond it (ADR-0095). Raising a line, recording a plan and resuming a
    // paused vendor spend a billing.change token; lowering and pausing do not.
    '/v1/vendors/list',
    '/v1/vendors/plan/set',
    '/v1/vendors/allowance/set',
    '/v1/vendors/pause',
    '/v1/vendors/resume',
    // What is switched on and what is left (ADR-0097): read from what the
    // platform can see for itself. A read, so no token.
    '/v1/readiness',
    // The platform acting as one person in their own Google calendar or drive
    // (ADR-0090): a superadmin's to grant, under a `delegation.grant` token
    // bound to `delegation:<personId>:<surface>`, and to take back, spending
    // none. The list is the person page's.
    '/v1/delegations/list',
    '/v1/delegations/grant',
    '/v1/delegations/revoke',
    // The Workspace's setup roadmap (ADR-0117): every step a person takes,
    // its state read from Readiness and from a superadmin's marks. Marking a
    // step no check can see spends no token; a mark is a person's word, kept.
    '/v1/setup',
    '/v1/setup/mark',
]);
/**
 * The admin door's reads a superadmin's own assistant may make over MCP
 * (ADR-0170). Every other admin path acts — stops, grants, admits, mints —
 * and stays a person's press in a signed-in browser (ADR-0073); these only
 * say what is switched on, what is left, and what waits. The gateway offers
 * them only to a superadmin, never on a personal account's connection, and
 * a test holds every admin path to being either one of these or a control
 * the MCP door names as browser-only, so a new admin path is decided rather
 * than defaulted.
 */
export const ADMIN_READ_PATHS = new Set([
    '/v1/readiness',
    '/v1/setup',
    '/v1/vendors/list',
    '/v1/connections/list',
    '/v1/hosted-clients/list',
    '/v1/tasks/tools/pending',
]);
/**
 * The few `APP_PATHS` core-api answers itself on the way through
 * `/app/forward`, rather than carrying to work-api. Four spend a
 * supervision token, and core-api is the process that consumes them (§17):
 * one atomic statement, in the same transaction as the write it authorises.
 * work-api holds no path to it, on purpose. The fifth, the map key, is read
 * from Secret Manager, which only core-api reaches (ADR-0083, ADR-0137).
 */
export const CORE_API_PATHS = new Set(['/v1/counterparties/approve', '/v1/prospects/approve-company', '/v1/unresolved/know-and-approve', '/v1/outreach/campaigns/approve', '/v1/mailings/approve', '/v1/maps/key']);
/** Where a person's browser reads the territory map's public key (ADR-0137). */
export const MAPS_KEY_PATH = '/v1/maps/key';
/**
 * The doors a vendor calls, never a person (ADR-0087). Each is signed by
 * the vendor over the raw body, verified by core-api with a key a
 * superadmin set on Connections, and may only narrow: a hook writes a
 * suppression or a figure and starts nothing (ADR-0081, ADR-0058).
 * One may *ask* (ADR-0099): a journey names an approved mailing and a
 * person, and confers nothing — the send is judged again from the rows.
 * Disjoint from both person doors, and never forwarded as an app call.
 */
export const HOOK_PATHS = new Set(['/v1/hooks/customerio', '/v1/hooks/customerio/journey', '/v1/hooks/google-calendar', '/v1/hooks/unsubscribe', '/v1/hooks/form']);
/**
 * Where a recipient's mail client posts a one-click unsubscribe (RFC 8058,
 * ADR-0112). Nobody signs it: what vouches for it is the random token the
 * send carried, whose hash the message keeps. It may only narrow — the one
 * thing it does is suppress the address that send went to — and a GET
 * changes nothing, because link scanners fetch every URL in a message.
 */
export const UNSUBSCRIBE_HOOK_PATH = '/v1/hooks/unsubscribe';
/**
 * Where the platform's own website posts a form entry — the sample form,
 * the newsletter box — as consent evidence (ADR-0113, discovery Q6). The
 * site's backend signs the raw body and a timestamp with a key a superadmin
 * set on Connections (`website.form_eu`); core-api verifies it. No third
 * party stands between the form and the platform.
 */
export const FORM_HOOK_PATH = '/v1/hooks/form';
/** work-api's side of a verified form entry. Reached only from core-api; no door leads here. */
export const FORM_HEARD_PATH = '/v1/lifecycle/form';
/**
 * The public booking page (ADR-0114): `GET` shows a person's bookable
 * times, `POST` holds one. No session and no signature — what a stranger
 * can do here is exactly one held booking inside the person's own hours,
 * bounded per link and per address, and the page says nothing about the
 * person but their name. Under `/v1/` so the Worker, not the interface's
 * fallback, answers it.
 */
export const BOOKING_PAGE_PREFIX = '/v1/public/book/';
/**
 * The platform's own file door (ADR-0114): `GET` with the random token an
 * attachment's link carries returns that one uploaded file, as a download,
 * while the link lives. Nothing else is reachable from it.
 */
export const FILE_DOOR_PREFIX = '/v1/public/file/';
/** work-api's side of the booking page and of the file door. Reached only from core-api; no door leads here. */
export const BOOKING_PAGE_HEARD_PATH = '/v1/ingest/booking-page';
export const BOOKING_MADE_HEARD_PATH = '/v1/ingest/booking';
export const FILE_HEARD_PATH = '/v1/ingest/file';
/** work-api's side of a one-click unsubscribe. Reached only from core-api; no door leads here. */
export const UNSUBSCRIBE_HEARD_PATH = '/v1/ingest/unsubscribe';
/** Where a Customer.io journey's webhook action asks for a mailing (ADR-0099). */
export const JOURNEY_HOOK_PATH = '/v1/hooks/customerio/journey';
/**
 * work-api's side of a verified journey request. Reached only from
 * core-api, after the signature is checked; no door leads here.
 */
export const MAILING_ASKED_PATH = '/v1/mailings/asked';
/**
 * Where Google Calendar says a watched calendar changed (ADR-0096). A POST
 * with headers and no body; what vouches for it is the channel token the
 * platform chose, which work-api checks against the hash it kept. It only
 * queues a read of the person's own calendars, as the backstop would.
 */
export const CALENDAR_HOOK_PATH = '/v1/hooks/google-calendar';
/**
 * work-api's side of a calendar notification. Reached only from core-api;
 * no door leads here.
 */
export const CALENDAR_HEARD_PATH = '/v1/ingest/calendar';
/**
 * work-api's side of the readiness report (ADR-0097). Reached only from
 * core-api's admin door, after the gateway found a superadmin's session.
 */
export const READINESS_REPORT_PATH = '/v1/readiness/report';
/**
 * work-api's side of a verified Customer.io hook. Reached only from
 * core-api, after the signature is checked; no door leads here.
 */
export const LIFECYCLE_REPORTED_PATH = '/v1/lifecycle/reported';
//# sourceMappingURL=app-paths.js.map